import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UsersRound, ArrowRight } from "lucide-react";

/** Join a group via its shareable invite token. Requires auth (ProtectedRoute). */
export default function GroupJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("prompt"); // prompt | joining | joined | error
  const [error, setError] = useState(null);

  const join = async () => {
    setStatus("joining");
    try {
      const { data } = await api.post(`/groups/join/${token}`);
      setStatus("joined");
      toast.success(data.already_member ? "You're already in this group" : "Welcome to the group!");
      setTimeout(() => navigate(`/groups/${data.group_id}`), 500);
    } catch (e) {
      setStatus("error");
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    // Auto-join once on load.
    join();
    // eslint-disable-next-line
  }, []);

  return (
    <AppLayout>
      <Card className="max-w-md mx-auto p-8 border border-slate-200 rounded-xl bg-white mt-8 text-center" data-testid="group-join-card">
        <div
          className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-white mb-4"
          style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}
        >
          <UsersRound className="w-6 h-6" />
        </div>
        {status === "joining" && (
          <>
            <div className="font-display font-bold text-lg text-slate-900 mb-1">Joining group…</div>
            <div className="text-sm text-slate-500">One moment.</div>
          </>
        )}
        {status === "joined" && (
          <>
            <div className="font-display font-bold text-lg text-slate-900 mb-1">You're in!</div>
            <div className="text-sm text-slate-500">Redirecting to your group…</div>
          </>
        )}
        {status === "error" && (
          <>
            <div className="font-display font-bold text-lg text-slate-900 mb-1">This invite didn't work</div>
            <div className="text-sm text-slate-500 mb-4">{error}</div>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => navigate("/groups")} variant="outline" data-testid="group-join-back">
                Back to Groups
              </Button>
              <Button onClick={join} className="text-white bg-[#0D9488]" data-testid="group-join-retry">
                Try again <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </>
        )}
      </Card>
    </AppLayout>
  );
}
